import React, { useEffect, useState, useRef } from "react";
import GoogleLogin from "react-google-login";
import { useDispatch, useSelector } from "react-redux";
import {
  useNavigate,
  useLocation,
  useSearchParams,
  Link,
} from "react-router-dom";
import ReactPixel from "react-facebook-pixel";
import { toast } from "react-toastify";
import { useCookies } from "react-cookie";
import {
  X,
  Eye,
  EyeOff,
  ArrowRight,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
  Check,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Lock,
  Trash2,
  ArrowLeft,
  Info,
  HelpCircle,
  FileText,
  AlertCircle,
  ExternalLink,
  LogOut,
  User,
  Sparkles,
  LifeBuoy,
  MessageCircle,
} from "lucide-react";

import { XANO_GOOGLE_CLIENT_ID } from "../../config/api_config";
import {
  getXanoAuthEmailToken,
  getXanoAuthToken,
  getXanoAuthTokenFromUrl,
  xanoUserSignup,
  logout,
} from "../../redux/actions/authentication";
import { deleteUser } from "../../redux/actions/profileDetails";
import { Mixpanel } from "../../config/Mixpanel";
import Constants from "../../common/constants";
import PasswordResetModal from "../LandingPage25/PasswordResetModal";
import { allFooterLinks } from "../../components/Footer/links";
import "./DeletePage.css";
import { SET_IS_DELETE_ACCOUNT } from "../../redux/actions/types";

const DELETION_REASONS = [
  "Privacy & data security concerns",
  "Too many emails or notifications",
  "I achieved my learning goals",
  "Found an alternative learning platform",
  "Courses are too difficult or too easy",
  "It's too expensive / budget constraints",
  "Other reason",
];

const POLICY_FAQS = [
  {
    question: "What happens to my certificates and learning progress?",
    answer:
      "Upon deleting your account, all completed courses, earned certificates, test scores, and learning hours are permanently erased. Your public certificate verification URLs will become invalid and cannot be restored.",
  },
  {
    question: "What happens to active subscriptions or purchased credits?",
    answer:
      "In accordance with our Refunds & Cancellations Policy, any remaining period on LearnTube Pro, X, or Max plans as well as unspent doubts or credits are forfeited upon account deletion. If eligible within 3 days of purchase, please contact hello@careerninja.in prior to deleting.",
  },
  {
    question: "How is my personal data handled under GDPR & Indian IT laws?",
    answer:
      "Per Section 10 of our Privacy Policy and relevant data protection laws, all Personally Identifiable Information (PII) including name, email, phone, and login credentials will be permanently expunged from active databases within 30 days. Community posts or public forum contributions are irreversibly anonymized.",
  },
  {
    question: "Can I reactivate or recover my account later?",
    answer:
      "No. Account deletion is permanent and irreversible. Once confirmed, you will need to register as a new learner if you choose to return to LearnTube in the future.",
  },
];

export default function DeletePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [cookies, setCookie, removeCookie] = useCookies();

  // Redux auth & user state
  const user_token = useSelector((store) => store?.authentication?.user_token);
  const profileDetails = useSelector((store) => store?.profileDetails);
  const isLoggedIn = Boolean(user_token || cookies?.authToken);

  // Authentication states (for unauthenticated users - based on Login.jsx)
  const initialMode =
    searchParams.get("mode") === "signup" ? "signup" : "login";
  const [authMode, setAuthMode] = useState(initialMode);
  const [isLoadingAuth, setIsLoadingAuth] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordResetModalEnabled, setIsPasswordResetModalEnabled] =
    useState(false);
  const [isEmailError, setIsEmailError] = useState(false);

  const [signupData, setSignupData] = useState({
    name: "",
    email: searchParams.get("email") || "",
    password: "",
  });

  const [loginData, setLoginData] = useState({
    email: searchParams.get("email") || "",
    password: "",
  });

  // Deletion states (for authenticated users - based on DeletionModal.jsx)
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [deletion_reason, setDeletion_reason] = useState("");
  const [confirmationKeyword, setConfirmationKeyword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [countDown, setCountDown] = useState(5);
  const [activeFaq, setActiveFaq] = useState(0);

  const countdownIntervalRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("savedLandingPageURL", window.location.href);
    try {
      ReactPixel.pageView();
    } catch (err) {
      // ignore
    }
  }, []);

  // Post-deletion countdown redirect
  useEffect(() => {
    if (confirmDeletion) {
      countdownIntervalRef.current = setInterval(() => {
        setCountDown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            performLogoutRedirect();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
    };
  }, [confirmDeletion]);

  const performLogoutRedirect = () => {
    dispatch(logout());
    removeCookie("authToken", { path: "/" });
    navigate("/courses");
  };

  // Handle auto-login via lt_code / wa_code URL parameters
  // useEffect(() => {
  //   if (searchParams.get("lt_code")) {
  //     toast.success("Logging in Please Wait!");
  //     dispatch(
  //       getXanoAuthTokenFromUrl(
  //         "ltcode",
  //         searchParams.get("lt_code"),
  //         (successObj) => {
  //           if (successObj?.show_toast) {
  //             toast.error(successObj?.message);
  //           } else if (!successObj?.["number_verification"]) {
  //             navigate("/verification");
  //           } else if (
  //             searchParams.get("redirect") &&
  //             searchParams.get("redirect") === "/verification" &&
  //             successObj?.["number_verification"]
  //           ) {
  //             navigate("/courses");
  //           } else if (searchParams.get("redirect")) {
  //             navigate(`${searchParams.get("redirect")}`, { replace: true });
  //           } else {
  //             navigate("/delete");
  //           }
  //         }
  //       )
  //     );
  //   }
  //   if (searchParams.get("wa_code")) {
  //     toast.success("Logging in Please Wait!");
  //     dispatch(
  //       getXanoAuthTokenFromUrl(
  //         "wacode",
  //         searchParams.get("wa_code"),
  //         (successObj) => {
  //           if (successObj?.show_toast) {
  //             toast.error(successObj?.message);
  //           } else if (!successObj?.["number_verification"]) {
  //             navigate("/verification");
  //           } else if (
  //             searchParams.get("redirect") &&
  //             searchParams.get("redirect") === "/verification" &&
  //             successObj?.["number_verification"]
  //           ) {
  //             navigate("/courses");
  //           } else if (searchParams.get("redirect")) {
  //             navigate(`${searchParams.get("redirect")}`, { replace: true });
  //           } else {
  //             navigate("/delete");
  //           }
  //         }
  //       )
  //     );
  //   }
  // }, [dispatch, navigate, searchParams]);

  // Unified post-authentication success handler
  const handleAuthSuccess = (successObj) => {
    try {
      Mixpanel.identify(successObj?.email);
      Mixpanel.track(Constants.Mixpanel.LOG_IN, {
        distinct_id: successObj?.email,
      });
      Mixpanel.people.set({
        $first_name: successObj?.first_name,
        $last_name: successObj?.last_name,
        $email: successObj?.email,
        $name: successObj?.name,
      });
    } catch (e) {
      // ignore
    }

    if (successObj?.number_verification) {
    } else {
      if (successObj?.show_toast) {
        toast.error(successObj?.message);
        if (successObj?.message === "Email already in use") {
          setIsEmailError(true);
        }
        setIsLoadingAuth(false);
      } else {
        dispatch({ type: SET_IS_DELETE_ACCOUNT, payload: true });
        navigate("/verification");
      }
    }
  };

  // Google OAuth
  const handleGoogleAuthResponse = async (response) => {
    if (response.error || (response.status && response.status === "unknown")) {
      console.log("Auth Failed: ", response.error);
      setIsLoadingAuth(false);
    } else {
      const tokenID = response?.tokenObj?.access_token;
      setIsLoadingAuth(true);
      await dispatch(
        getXanoAuthToken(
          tokenID,
          (successObj) => {
            handleAuthSuccess(successObj);
          },
          (err) => {
            setIsLoadingAuth(false);
            toast.error(err?.message || "Google authentication failed");
          },
        ),
      );
    }
  };

  // Signup Submit
  const handleSignupSubmit = async (e) => {
    e?.preventDefault();
    setIsEmailError(false);

    if (!signupData.name.trim()) {
      return toast.error('"Full Name" field is required');
    }
    if (!signupData.email.trim()) {
      return toast.error('"Email" field is required');
    }
    if (!signupData.password.trim()) {
      return toast.error('"Password" field is required');
    }

    setIsLoadingAuth(true);
    await dispatch(
      xanoUserSignup(
        {
          name: signupData.name.trim(),
          email: signupData.email.trim(),
          phone: "",
          password: signupData.password,
        },
        (successObj) => {
          handleAuthSuccess(successObj);
        },
        (errObj) => {
          if (errObj?.message === "Email already in use") {
            setIsEmailError(true);
          }
          toast.error(errObj?.message || "Signup failed. Please try again.");
          setIsLoadingAuth(false);
        },
      ),
    );
  };

  // Login Submit
  const handleLoginSubmit = async (e) => {
    e?.preventDefault();
    if (!loginData.email.trim()) {
      return toast.error('"Email" field is required');
    }
    if (!loginData.password.trim()) {
      return toast.error('"Password" field is required');
    }

    setIsLoadingAuth(true);
    await dispatch(
      getXanoAuthEmailToken(
        loginData.email.trim(),
        loginData.password,
        (successObj) => {
          handleAuthSuccess(successObj);
        },
        (errorObj) => {
          setIsLoadingAuth(false);
          if (
            errorObj?.response?.data?.message === "Invalid password syntax."
          ) {
            toast.error("Try Login with Google");
          } else {
            toast.error(
              errorObj?.response?.data?.message ||
                "Invalid credentials provided.",
            );
          }
        },
      ),
    );
  };

  // Delete Action Handler (from DeletionModal.jsx)
  const handleDeleteAccount = () => {
    if (confirmationKeyword.trim().toLowerCase() !== "delete") {
      toast.error("Enter Delete Keyword");
      return;
    }

    if (!deletion_reason.trim()) {
      toast.error("Enter reason for deletion");
      return;
    }

    setIsDeleting(true);

    dispatch(
      deleteUser(
        deletion_reason.trim(),
        (successObj) => {
          setIsDeleting(false);
          setConfirmDeletion(true);
          setCountDown(5);
        },
        (errorObj) => {
          setIsDeleting(false);
          const errText =
            typeof errorObj === "string"
              ? errorObj
              : errorObj?.message ||
                errorObj?.response?.data?.message ||
                "Failed to delete account. Please try again.";
          toast.error(errText);
        },
      ),
    );
  };

  const handleLogout = () => {
    dispatch(logout());
    removeCookie("authToken", { path: "/" });
    toast.info("Logged out successfully");
  };

  // Fallback avatar initial
  const userDisplayName =
    profileDetails?.name ||
    profileDetails?.first_name ||
    (profileDetails?.email ? profileDetails.email.split("@")[0] : "Learner");
  const userEmail = profileDetails?.email || "Account on file";

  return (
    <div className="min-h-screen bg-[#F4F7FB] pb-20 font-sans text-[#0E1B2E] antialiased selection:bg-red-500 selection:text-white">
      {/* Top Banner & Header */}
      {/* <header className="border-b border-[#DDE4ED] bg-white sticky top-0 z-30 shadow-xs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-[#EDF1F7] text-[#4A5A70] hover:text-[#0E1B2E] transition-colors cursor-pointer"
              title="Go back"
            >
              <ArrowLeft size={18} />
            </button>
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-[30px] h-[30px] rounded-[8px] bg-[#203C5C] flex items-center justify-center overflow-hidden">
                <img
                  src="https://img.learntube.ai/public-assets/new%20Linkedin%20data/image%2011.png?updatedAt=1747309992449"
                  alt="LearnTube"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="font-bold text-[17px] text-[#0E1B2E] tracking-tight">
                LearnTube<span className="text-[#13A0BD]">.ai</span>
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-3 text-xs sm:text-sm">
            <Link
              to="/termsandconditions"
              className="text-[#4A5A70] hover:text-[#086788] font-semibold transition-colors hidden sm:inline-block"
            >
              Terms
            </Link>
            <Link
              to="/privacypolicy"
              className="text-[#4A5A70] hover:text-[#086788] font-semibold transition-colors hidden sm:inline-block"
            >
              Privacy
            </Link>
            <Link
              to="/refundpolicy"
              className="text-[#4A5A70] hover:text-[#086788] font-semibold transition-colors hidden sm:inline-block"
            >
              Refund Policy
            </Link>

            {isLoggedIn ? (
              <div className="flex items-center gap-2 pl-3 border-l border-[#DDE4ED]">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#13A0BD] to-[#086788] text-white flex items-center justify-center font-bold text-xs uppercase shadow-xs">
                  {userDisplayName.charAt(0)}
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="p-1.5 text-[#78899F] hover:text-[#E06064] hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                  title="Sign out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setAuthMode(authMode === "login" ? "signup" : "login")}
                  className="px-3 py-1.5 rounded-full border border-[#13A0BD] text-[#086788] font-bold text-xs hover:bg-[#E6F4F8] transition-colors cursor-pointer"
                >
                  {authMode === "login" ? "Create Account" : "Log In"}
                </button>
              </div>
            )}
          </div>
        </div>
      </header> */}

      {/* Main Container */}
      <main className="mx-auto max-w-6xl px-4 pt-8 pb-16 sm:px-6">
        {/* Breadcrumb & Section Header */}
        <div className="mb-8">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div>
              <h1 className="flex items-center gap-2.5 text-2xl font-extrabold tracking-tight text-[#0E1B2E] sm:text-3xl">
                <ShieldAlert className="text-[#E06064]" size={28} />
                Account & Data Deletion
              </h1>
              <p className="mt-1 max-w-2xl text-sm text-[#4A5A70] sm:text-base">
                Manage your privacy preferences, review data retention rules,
                and permanently close your LearnTube profile.
              </p>
            </div>

            {/* Quick Policy Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                <ShieldCheck size={13} />
                GDPR & DPDP Compliant
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
                <AlertTriangle size={13} />
                Irreversible Action
              </span>
            </div>
          </div>
        </div>

        {/* Content Grid: 2 Columns on desktop */}
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12">
          {/* ========================================================================= */}
          {/* LEFT COLUMN: INTERACTIVE FLOW (AUTH OR DELETE)                             */}
          {/* ========================================================================= */}
          <div className="flex flex-col gap-6 lg:col-span-7">
            {/* If Confirmed Deletion is Active */}
            {confirmDeletion ? (
              <div className="animate-in fade-in zoom-in-95 rounded-3xl border border-[#DDE4ED] bg-white p-8 text-center shadow-xl duration-200 sm:p-10">
                <div className="mx-auto mb-6 flex h-28 w-28 items-center justify-center rounded-full border-4 border-emerald-100 bg-emerald-50 p-4">
                  <img
                    src="https://img.learntube.ai/public-assets/Profile-section/task-done.svg?updatedAt=1690268616774"
                    alt="Account Deleted"
                    className="h-full w-full object-contain"
                  />
                </div>
                <h2 className="text-2xl font-extrabold text-[#0E1B2E] sm:text-3xl">
                  Account Successfully Deleted
                </h2>
                <p className="mx-auto mt-2 max-w-md text-sm text-[#4A5A70] sm:text-base">
                  Your profile and personal records have been queued for
                  permanent erasure. Thank you for learning with us.
                </p>

                <div className="mt-8 inline-flex items-center gap-2 rounded-2xl border border-[#DDE4ED] bg-[#F8FAFC] p-4 text-sm font-semibold text-[#086788]">
                  <span>Redirecting to courses in {countDown}s</span>
                  <span className="delete-dot-1">.</span>
                  <span className="delete-dot-2">.</span>
                  <span className="delete-dot-3">.</span>
                </div>

                <div className="mt-6">
                  <button
                    type="button"
                    onClick={performLogoutRedirect}
                    className="cursor-pointer text-xs font-bold text-[#4A5A70] underline hover:text-[#0E1B2E]"
                  >
                    Click here if not redirected automatically
                  </button>
                </div>
              </div>
            ) : isLoggedIn ? (
              /* ===================================================================== */
              /* STATE 1: USER IS LOGGED IN -> DISPLAY DELETION FUNCTIONALITY (Modal UI) */
              /* ===================================================================== */
              <div className="overflow-hidden rounded-3xl border border-rose-200/90 bg-white shadow-[0_16px_40px_-12px_rgba(224,96,100,0.15)]">
                {/* Danger Zone Header Banner */}
                <div className="bg-gradient-to-r from-rose-500 via-[#E06064] to-[#C9474B] p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="backdrop-blur-xs flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 text-white">
                        <Trash2 size={22} />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold leading-tight">
                          Delete Account Confirmation
                        </h2>
                        <p className="mt-0.5 text-xs text-white/80">
                          Permanent erasure of credentials and data
                        </p>
                      </div>
                    </div>
                    <span className="rounded-md bg-white/20 px-2.5 py-1 text-xs font-bold uppercase tracking-wider">
                      Danger Zone
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-6 p-6 sm:p-8">
                  {/* Account In Question Badge */}
                  <div className="flex items-center justify-between rounded-2xl border border-[#DDE4ED] bg-[#F8FAFC] p-4">
                    <div className="flex items-center gap-3">
                      <div className="shadow-xs flex h-11 w-11 items-center justify-center rounded-full bg-[#086788] text-base font-bold text-white">
                        {userDisplayName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-[#78899F]">
                          Currently Signed In
                        </p>
                        <h3 className="text-sm font-bold text-[#0E1B2E] sm:text-base">
                          {userDisplayName}
                        </h3>
                        <p className="text-xs text-[#4A5A70]">{userEmail}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex cursor-pointer items-center gap-1 text-xs font-bold text-[#086788] hover:text-[#064e67] hover:underline"
                    >
                      <LogOut size={13} />
                      Switch Account
                    </button>
                  </div>

                  {/* Illustration & Warning Copy */}
                  <div className="flex flex-col items-center gap-6 rounded-2xl border border-rose-200 bg-rose-50/70 p-4 sm:flex-row">
                    <div className="h-24 w-24 shrink-0">
                      <img
                        src="https://img.learntube.ai/public-assets/Profile-section/delete-user.svg?updatedAt=1690268616824"
                        alt="Delete Account Alert"
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <div>
                      <h4 className="text-base font-bold leading-snug text-rose-900">
                        Are you sure you want to delete your LearnTube account?
                      </h4>
                      <p className="mt-1 text-xs leading-relaxed text-rose-700 sm:text-sm">
                        If you proceed, your account cannot be recovered. You
                        will permanently forfeit all course certifications,
                        assessment records, and any remaining pro credits.
                      </p>
                    </div>
                  </div>

                  {/* Impact Checklist */}
                  <div className="grid grid-cols-1 gap-3 text-xs sm:grid-cols-2">
                    <div className="flex items-start gap-2.5 rounded-xl border border-[#DDE4ED] bg-[#F8FAFC] p-3">
                      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                        <X size={12} />
                      </div>
                      <span className="leading-snug text-[#4A5A70]">
                        <strong className="block text-[#0E1B2E]">
                          All Certificates Voided
                        </strong>
                        Verification links will instantly become invalid.
                      </span>
                    </div>

                    <div className="flex items-start gap-2.5 rounded-xl border border-[#DDE4ED] bg-[#F8FAFC] p-3">
                      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                        <X size={12} />
                      </div>
                      <span className="leading-snug text-[#4A5A70]">
                        <strong className="block text-[#0E1B2E]">
                          Learning Progress Erased
                        </strong>
                        Completed modules, streaks, and quiz results deleted.
                      </span>
                    </div>

                    <div className="flex items-start gap-2.5 rounded-xl border border-[#DDE4ED] bg-[#F8FAFC] p-3">
                      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                        <X size={12} />
                      </div>
                      <span className="leading-snug text-[#4A5A70]">
                        <strong className="block text-[#0E1B2E]">
                          Forfeiture of Pro Access
                        </strong>
                        Active subscription time & coins cancelled without
                        refund.
                      </span>
                    </div>

                    <div className="flex items-start gap-2.5 rounded-xl border border-[#DDE4ED] bg-[#F8FAFC] p-3">
                      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                        <X size={12} />
                      </div>
                      <span className="leading-snug text-[#4A5A70]">
                        <strong className="block text-[#0E1B2E]">
                          Identity Purged
                        </strong>
                        Login credentials expunged under GDPR privacy terms.
                      </span>
                    </div>
                  </div>

                  {/* Confirmation Keyword Input */}
                  <div className="flex w-full flex-col items-start justify-center">
                    <div className="mb-1.5 text-[14px] font-medium text-[#0E1B2E]">
                      To confirm, type{" "}
                      <span className="font-bold text-[#E06064]">Delete</span>{" "}
                      in the box below
                      <span className="ml-0.5 font-bold text-[#E06064]">*</span>
                    </div>
                    <div className="relative w-full">
                      <input
                        type="text"
                        required
                        placeholder='Type "Delete"'
                        value={confirmationKeyword}
                        onChange={(e) => setConfirmationKeyword(e.target.value)}
                        className="h-11 w-full rounded-xl border-2 border-rose-300 bg-white px-3.5 text-sm font-semibold uppercase tracking-wider text-[#0E1B2E] transition-all focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-200"
                      />
                      {confirmationKeyword.trim().toLowerCase() ===
                        "delete" && (
                        <span className="absolute right-3.5 top-3 flex items-center gap-1 text-xs font-bold text-emerald-600">
                          <Check size={16} /> Ready
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Reason for Deletion */}
                  <div className="flex w-full flex-col items-start justify-center">
                    <div className="mb-1.5 flex w-full items-center justify-between text-[14px] font-medium text-[#0E1B2E]">
                      <span>Reason for deletion</span>
                      <span className="text-[11px] text-[#78899F]">
                        Click a chip or type below
                      </span>
                    </div>

                    {/* Quick reason suggestions */}
                    <div className="mb-2.5 flex w-full flex-wrap gap-1.5">
                      {DELETION_REASONS.map((r) => {
                        const isSelected = deletion_reason === r;
                        return (
                          <button
                            key={r}
                            type="button"
                            onClick={() => setDeletion_reason(r)}
                            className={`cursor-pointer rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${
                              isSelected
                                ? "shadow-xs border-rose-400 bg-rose-50 font-bold text-rose-900"
                                : "border-[#DDE4ED] bg-white text-[#4A5A70] hover:bg-[#F8FAFC]"
                            }`}
                          >
                            {r}
                          </button>
                        );
                      })}
                    </div>

                    <input
                      type="text"
                      name="deletion_reason"
                      placeholder="Reason for deletion"
                      value={deletion_reason}
                      onChange={(e) => setDeletion_reason(e.target.value)}
                      className="h-11 w-full rounded-xl border-2 border-[#CBD5E1] bg-white px-3.5 text-sm text-[#0E1B2E] transition-all focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-200"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col items-center gap-3 pt-2 sm:flex-row">
                    <button
                      type="button"
                      disabled={
                        isDeleting ||
                        confirmationKeyword.trim().toLowerCase() !== "delete"
                      }
                      onClick={handleDeleteAccount}
                      className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#E06064] text-sm font-bold text-white shadow-[0_4px_14px_rgba(224,96,100,0.35)] transition-all hover:bg-[#d5484c] disabled:cursor-not-allowed disabled:opacity-40 sm:w-1/2 sm:text-base"
                    >
                      {isDeleting ? (
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      ) : (
                        <>
                          <Trash2 size={17} />
                          <span>Delete My Account</span>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => navigate("/my-profile/user")}
                      className="h-12 w-full cursor-pointer rounded-xl border border-[#CBD5E1] bg-white text-sm font-bold text-[#4A5A70] transition-colors hover:bg-[#F8FAFC] sm:w-1/2 sm:text-base"
                    >
                      Cancel & Keep Account
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* ===================================================================== */
              /* STATE 2: USER NOT LOGGED IN -> DISPLAY LOGIN / SIGNUP (Login.jsx UI) */
              /* ===================================================================== */
              <div className="relative rounded-3xl border border-[#DDE4ED] bg-white p-6 shadow-xl sm:p-9">
                {/* Brand Badge */}
                <div className="mb-6 flex items-center justify-between border-b border-[#EDF1F7] pb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-[30px] w-[30px] items-center justify-center overflow-hidden rounded-[8px] bg-[#203C5C]">
                      <img
                        src="https://img.learntube.ai/public-assets/new%20Linkedin%20data/image%2011.png?updatedAt=1747309992449"
                        alt="LearnTube"
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <span className="text-[17px] font-bold tracking-tight text-[#0E1B2E]">
                      LearnTube.ai
                    </span>
                  </div>

                  {/* Mode Pill Toggle */}
                  <div className="flex items-center rounded-full bg-[#EDF1F7] p-1 text-xs font-bold">
                    <button
                      type="button"
                      onClick={() => setAuthMode("login")}
                      className={`cursor-pointer rounded-full px-3 py-1.5 transition-all ${
                        authMode === "login"
                          ? "shadow-xs bg-white text-[#0E1B2E]"
                          : "text-[#78899F] hover:text-[#0E1B2E]"
                      }`}
                    >
                      Sign In
                    </button>
                    <button
                      type="button"
                      onClick={() => setAuthMode("signup")}
                      className={`cursor-pointer rounded-full px-3 py-1.5 transition-all ${
                        authMode === "signup"
                          ? "shadow-xs bg-white text-[#0E1B2E]"
                          : "text-[#78899F] hover:text-[#0E1B2E]"
                      }`}
                    >
                      Sign Up
                    </button>
                  </div>
                </div>

                {/* Authentication Intro */}
                <div className="mb-6">
                  <div className="mb-2 inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
                    <Lock size={12} />
                    Authentication Required
                  </div>
                  <h2 className="text-2xl font-bold tracking-tight text-[#0E1B2E]">
                    {authMode === "signup"
                      ? "Create an account or identify yourself"
                      : "Sign in to access account deletion"}
                  </h2>
                  <p className="mt-1 text-xs leading-relaxed text-[#4A5A70] sm:text-sm">
                    To safeguard user data and prevent unauthorized requests,
                    you must authenticate to verify ownership of the account you
                    wish to delete.
                  </p>
                </div>

                {/* Google Sign-in */}
                <GoogleLogin
                  clientId={XANO_GOOGLE_CLIENT_ID}
                  render={(renderProps) => (
                    <button
                      type="button"
                      onClick={renderProps.onClick}
                      disabled={renderProps.disabled || isLoadingAuth}
                      className="flex h-[50px] w-full cursor-pointer items-center justify-center gap-2.5 rounded-full text-[15px] font-bold text-white shadow-[0_3px_12px_rgba(8,103,136,0.25)] transition-all hover:opacity-95 active:scale-[0.99] disabled:opacity-50 sm:text-[16px]"
                      style={{
                        background:
                          "linear-gradient(121.35deg, #13A0BD 16.15%, #086788 86.12%)",
                      }}
                    >
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white">
                        <svg viewBox="0 0 24 24" width="18" height="18">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                          />
                        </svg>
                      </div>
                      <span>
                        {authMode === "signup"
                          ? "Continue with Google"
                          : "Sign in with Google"}
                      </span>
                    </button>
                  )}
                  onSuccess={handleGoogleAuthResponse}
                  onFailure={handleGoogleAuthResponse}
                  cookiePolicy={"single_host_origin"}
                />

                {/* Divider */}
                <div className="my-5 flex items-center">
                  <div className="h-[1px] flex-1 bg-[#DDE4ED]" />
                  <span className="px-3 text-[12px] font-semibold text-[#78899F]">
                    {authMode === "signup"
                      ? "or register with email"
                      : "or continue with email"}
                  </span>
                  <div className="h-[1px] flex-1 bg-[#DDE4ED]" />
                </div>

                {/* Forms */}
                {authMode === "signup" ? (
                  /* =============================================================== */
                  /* SIGNUP FORM                                                     */
                  /* =============================================================== */
                  <form
                    onSubmit={handleSignupSubmit}
                    className="flex flex-col gap-3.5"
                  >
                    <div>
                      <label className="mb-1.5 block text-[13px] font-semibold text-[#0E1B2E]">
                        Your Name<span className="text-[#E06064]">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="For e.g., John Doe"
                        value={signupData.name}
                        onChange={(e) =>
                          setSignupData({ ...signupData, name: e.target.value })
                        }
                        className="h-[44px] w-full rounded-[10px] border border-[#CBD5E1] px-3.5 text-[14px] font-medium text-[#0E1B2E] transition-colors focus:border-[#13A0BD] focus:outline-none focus:ring-1 focus:ring-[#13A0BD]"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-[13px] font-semibold text-[#0E1B2E]">
                        Email<span className="text-[#E06064]">*</span>
                        {isEmailError && (
                          <span className="ml-2 text-[12px] font-normal italic text-[#E06064]">
                            Already in use, try to login
                          </span>
                        )}
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="For e.g., johndoe123@gmail.com"
                        value={signupData.email}
                        onChange={(e) => {
                          setIsEmailError(false);
                          setSignupData({
                            ...signupData,
                            email: e.target.value,
                          });
                        }}
                        className={`h-[44px] w-full rounded-[10px] border px-3.5 text-[14px] font-medium text-[#0E1B2E] transition-colors focus:border-[#13A0BD] focus:outline-none focus:ring-1 focus:ring-[#13A0BD] ${
                          isEmailError ? "border-[#E06064]" : "border-[#CBD5E1]"
                        }`}
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-[13px] font-semibold text-[#0E1B2E]">
                        Password<span className="text-[#E06064]">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          required
                          placeholder="Create a password"
                          value={signupData.password}
                          onChange={(e) =>
                            setSignupData({
                              ...signupData,
                              password: e.target.value,
                            })
                          }
                          className="h-[44px] w-full rounded-[10px] border border-[#CBD5E1] pl-3.5 pr-10 text-[14px] font-medium text-[#0E1B2E] transition-colors focus:border-[#13A0BD] focus:outline-none focus:ring-1 focus:ring-[#13A0BD]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-3 flex items-center text-[#78899F] hover:text-[#0E1B2E]"
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoadingAuth}
                      className="mt-2 flex h-[48px] w-full cursor-pointer items-center justify-center gap-2 rounded-full border-[1.5px] border-[#13A0BD] text-[15px] font-bold text-[#086788] transition-all hover:bg-[#E6F4F8] active:scale-[0.99] disabled:opacity-50"
                    >
                      <span>
                        {isLoadingAuth ? "Signing up..." : "Sign Up & Continue"}
                      </span>
                      <ArrowRight size={16} />
                    </button>

                    <div className="mt-2 text-center text-xs text-[#4A5A70]">
                      Already have an account?{" "}
                      <button
                        type="button"
                        onClick={() => setAuthMode("login")}
                        className="cursor-pointer font-bold text-[#06769E] hover:underline"
                      >
                        Log in here
                      </button>
                    </div>
                  </form>
                ) : (
                  /* =============================================================== */
                  /* LOGIN FORM                                                      */
                  /* =============================================================== */
                  <form
                    onSubmit={handleLoginSubmit}
                    className="flex flex-col gap-3.5"
                  >
                    <div>
                      <label className="mb-1.5 block text-[13px] font-semibold text-[#0E1B2E]">
                        Email Address<span className="text-[#E06064]">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="For e.g., johndoe123@gmail.com"
                        value={loginData.email}
                        onChange={(e) =>
                          setLoginData({ ...loginData, email: e.target.value })
                        }
                        className="h-[44px] w-full rounded-[10px] border border-[#CBD5E1] px-3.5 text-[14px] font-medium text-[#0E1B2E] transition-colors focus:border-[#13A0BD] focus:outline-none focus:ring-1 focus:ring-[#13A0BD]"
                      />
                    </div>

                    <div>
                      <div className="mb-1.5 flex items-center justify-between">
                        <label className="text-[13px] font-semibold text-[#0E1B2E]">
                          Password<span className="text-[#E06064]">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setIsPasswordResetModalEnabled(true)}
                          className="cursor-pointer text-[12px] font-semibold text-[#06769E] hover:underline"
                        >
                          Forgot Password?
                        </button>
                      </div>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          required
                          placeholder="Enter your password"
                          value={loginData.password}
                          onChange={(e) =>
                            setLoginData({
                              ...loginData,
                              password: e.target.value,
                            })
                          }
                          className="h-[44px] w-full rounded-[10px] border border-[#CBD5E1] pl-3.5 pr-10 text-[14px] font-medium text-[#0E1B2E] transition-colors focus:border-[#13A0BD] focus:outline-none focus:ring-1 focus:ring-[#13A0BD]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-3 flex items-center text-[#78899F] hover:text-[#0E1B2E]"
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoadingAuth}
                      className="mt-2 flex h-[48px] w-full cursor-pointer items-center justify-center gap-2 rounded-full border-[1.5px] border-[#13A0BD] text-[15px] font-bold text-[#086788] transition-all hover:bg-[#E6F4F8] active:scale-[0.99] disabled:opacity-50"
                    >
                      <span>
                        {isLoadingAuth ? "Verifying..." : "Verify & Continue"}
                      </span>
                      <ArrowRight size={16} />
                    </button>

                    <div className="mt-2 text-center text-xs text-[#4A5A70]">
                      Need an account first?{" "}
                      <button
                        type="button"
                        onClick={() => setAuthMode("signup")}
                        className="cursor-pointer font-bold text-[#06769E] hover:underline"
                      >
                        Sign up here
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>

          {/* ========================================================================= */}
          {/* RIGHT COLUMN: TERMS, POLICIES & DATA RETENTION OVERVIEW                   */}
          {/* ========================================================================= */}
          <div className="flex flex-col gap-6 lg:col-span-5">
            {/* Policy Reference Cards */}
            <div className="rounded-3xl border border-[#DDE4ED] bg-white p-6 shadow-sm sm:p-7">
              <div className="mb-4 flex items-center gap-2.5">
                <FileText className="text-[#086788]" size={20} />
                <h3 className="text-base font-bold text-[#0E1B2E]">
                  Official Policies & Terms
                </h3>
              </div>
              <p className="mb-4 text-xs leading-relaxed text-[#4A5A70]">
                LearnTube (operated by BeNinja Solutions Pvt. Ltd.) processes
                account modifications and deletions according to our legal
                agreements:
              </p>

              {/* Policy Links List */}
              <div className="flex flex-col gap-2.5">
                <Link
                  to="/privacypolicy"
                  className="group flex items-center justify-between rounded-2xl border border-[#DDE4ED] bg-[#F8FAFC] p-3 transition-colors hover:bg-[#EDF1F7]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-cyan-100 text-xs font-bold text-[#086788]">
                      §10
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-[#0E1B2E] group-hover:text-[#086788]">
                        Privacy Policy
                      </h4>
                      <p className="text-[11px] text-[#78899F]">
                        Section 10: Updating & Deleting Personal Information
                      </p>
                    </div>
                  </div>
                  <ExternalLink
                    size={14}
                    className="text-[#78899F] group-hover:text-[#086788]"
                  />
                </Link>

                <Link
                  to="/termsandconditions"
                  className="group flex items-center justify-between rounded-2xl border border-[#DDE4ED] bg-[#F8FAFC] p-3 transition-colors hover:bg-[#EDF1F7]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-100 text-xs font-bold text-indigo-700">
                      T&C
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-[#0E1B2E] group-hover:text-[#086788]">
                        Terms & Conditions
                      </h4>
                      <p className="text-[11px] text-[#78899F]">
                        Platform obligations, termination, and service scope
                      </p>
                    </div>
                  </div>
                  <ExternalLink
                    size={14}
                    className="text-[#78899F] group-hover:text-[#086788]"
                  />
                </Link>

                <Link
                  to="/refundpolicy"
                  className="group flex items-center justify-between rounded-2xl border border-[#DDE4ED] bg-[#F8FAFC] p-3 transition-colors hover:bg-[#EDF1F7]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-amber-100 text-xs font-bold text-amber-800">
                      REF
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-[#0E1B2E] group-hover:text-[#086788]">
                        Refunds & Cancellations
                      </h4>
                      <p className="text-[11px] text-[#78899F]">
                        LearnTube Pro, X, and Max subscription validity rules
                      </p>
                    </div>
                  </div>
                  <ExternalLink
                    size={14}
                    className="text-[#78899F] group-hover:text-[#086788]"
                  />
                </Link>
              </div>
            </div>

            {/* Interactive FAQs Accordion */}
            <div className="rounded-3xl border border-[#DDE4ED] bg-white p-6 shadow-sm sm:p-7">
              <div className="mb-4 flex items-center gap-2.5">
                <HelpCircle className="text-[#086788]" size={20} />
                <h3 className="text-base font-bold text-[#0E1B2E]">
                  Frequently Asked Questions
                </h3>
              </div>

              <div className="flex flex-col gap-2.5">
                {POLICY_FAQS.map((faq, idx) => {
                  const isOpen = activeFaq === idx;
                  return (
                    <div
                      key={faq.question}
                      className="overflow-hidden rounded-2xl border border-[#E2E8F0] transition-all"
                    >
                      <button
                        type="button"
                        onClick={() => setActiveFaq(isOpen ? null : idx)}
                        className="flex w-full cursor-pointer items-center justify-between gap-2 p-3.5 text-left text-xs font-semibold text-[#0E1B2E] transition-colors hover:bg-[#F8FAFC] sm:text-sm"
                      >
                        <span>{faq.question}</span>
                        {isOpen ? (
                          <ChevronUp
                            size={16}
                            className="shrink-0 text-[#78899F]"
                          />
                        ) : (
                          <ChevronDown
                            size={16}
                            className="shrink-0 text-[#78899F]"
                          />
                        )}
                      </button>
                      {isOpen && (
                        <div className="border-t border-[#F1F5F9] bg-[#F8FAFC]/50 px-3.5 pb-3.5 pt-1 text-xs leading-relaxed text-[#4A5A70]">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Support Lifeline Card */}
            <div className="rounded-3xl bg-gradient-to-br from-[#086788] to-[#043E54] p-6 text-white shadow-lg">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10">
                  <LifeBuoy size={20} className="text-cyan-300" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white sm:text-base">
                    Need help before deleting?
                  </h4>
                  <p className="mt-1 text-xs leading-relaxed text-white/80">
                    If you are experiencing course access issues, billing
                    confusion, or wish to export your certificates first, our
                    support team is here to assist.
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 border-t border-white/10 pt-4 text-xs">
                <a
                  href="mailto:hello@careerninja.in?subject=Help%20with%20my%20LearnTube%20Account"
                  className="bg-white/15 flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold text-white transition-colors hover:bg-white/25"
                >
                  <MessageCircle size={13} />
                  Email Support
                </a>
                <a
                  href={allFooterLinks.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-white/15 flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold text-white transition-colors hover:bg-white/25"
                >
                  WhatsApp Assistant
                </a>
                <Link
                  to="/contactus"
                  className="bg-white/15 flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold text-white transition-colors hover:bg-white/25"
                >
                  Contact Form
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Password Reset Modal from Login.jsx */}
      <PasswordResetModal
        isModalEnabled={isPasswordResetModalEnabled}
        setIsModalEnabled={setIsPasswordResetModalEnabled}
      />
    </div>
  );
}
